import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { IonInput, ModalController } from '@ionic/angular';
import { Anagrafica } from '../../../_models/Anagrafica';
import { SearchResult } from '../../../_models/SearchResult';
import { RegistryService } from '../../../_services/registry.service';
import { OrganizzazioniService } from '../../../_services/organizzazioni.service';
import { SaveMessage } from '../../../_helpers/save-message';
import { MessageConfirm } from '../../../_helpers/message-confirm.service';
import { AnagraficaCreateComponent, emptyAnagrafica } from '../../anagrafiche/anagrafica-create/anagrafica-create.component';

@Component({
  selector: 'app-select-anagrafica-registry',
  templateUrl: './select-anagrafica-registry.component.html',
  styleUrls: ['./select-anagrafica-registry.component.scss'],
  standalone: false
})
export class SelectAnagraficaRegistryComponent implements OnInit, OnDestroy {
  @Input() anagraficaId = 0;
  @Output() anagraficaIdChange = new EventEmitter<number>();
  @Output() anagraficaChange = new EventEmitter<Anagrafica>();
  @Output() ragioneSocialeLibera = new EventEmitter<string>();
  @Input() tipoFiltro = 0;
  @Input() label = 'Cliente';
  // Ragione sociale iniziale dell'entità ospite (es. ordine in modifica): con
  // anagraficaId = 0 inizializza lo stato "valore libero" senza emettere nulla.
  @Input() ragioneSociale = '';

  anagraficaSelezionata: Anagrafica | null = null;
  search = '';
  risultati: Anagrafica[] = [];
  showDropdown = false;
  loading = false;
  valoreLibero = '';

  private commitInCorso = false;
  private blurTimeout: ReturnType<typeof setTimeout> | null = null;

  @ViewChild('searchInput') private searchInput?: IonInput;

  constructor(
    private registryService: RegistryService,
    private organizzazioniService: OrganizzazioniService,
    private modalCtrl: ModalController,
    private saveMessage: SaveMessage,
    private messageConfirm: MessageConfirm
  ) {}

  ngOnInit() {
    if (this.anagraficaId > 0) {
      this.caricaAnagraficaSelezionata();
    } else if (this.ragioneSociale) {
      this.valoreLibero = this.ragioneSociale;
    }
  }

  ngOnDestroy() {
    this.annullaCommitPendente();
  }

  caricaAnagraficaSelezionata() {
    this.loading = true;
    this.registryService.getAnagraficaAsync(
      this.organizzazioniService.organizzazioneId, this.anagraficaId
    )
      .then(a => this.anagraficaSelezionata = a)
      .catch(() => {
        this.saveMessage.messageError('Errore nel caricamento del cliente.');
        // Fallback (US4): se l'ordine ha una ragione sociale salvata, mostrala come valore
        // libero (puramente visivo, senza emettere eventi che marcherebbero una modifica).
        if (this.ragioneSociale) this.valoreLibero = this.ragioneSociale;
      })
      .finally(() => this.loading = false);
  }

  private eseguiRicerca(testo: string): Promise<Anagrafica[]> {
    return this.registryService.getAnagraficheFilteredAsync(
      this.organizzazioniService.organizzazioneId,
      testo, 0, 20, 'ragioneSociale', true
    ).then((result: SearchResult<Anagrafica>) =>
      (result?.result ?? []).filter(
        a => this.tipoFiltro === 0 || (a.tipi & this.tipoFiltro) === this.tipoFiltro
      )
    );
  }

  onEnter() {
    this.annullaCommitPendente();
    this.commitRicerca();
  }

  onBlur() {
    this.annullaCommitPendente();
    this.blurTimeout = setTimeout(() => this.commitUscita(), 250);
  }

  private annullaCommitPendente() {
    if (this.blurTimeout !== null) {
      clearTimeout(this.blurTimeout);
      this.blurTimeout = null;
    }
  }

  async commitRicerca() {
    const testo = this.search.trim();
    if (this.commitInCorso || !testo) {
      return;
    }
    this.commitInCorso = true;
    this.loading = true;
    try {
      const risultati = await this.eseguiRicerca(testo);
      if (this.search.trim() !== testo) {
        return;
      }
      // commitInCorso resta true anche durante alert/modale (await): il blur
      // rubato dall'alert non può innescare un secondo commit (una sola richiesta).
      await this.gestisciEsito(testo, risultati);
    } catch {
      this.saveMessage.messageError('Errore nel caricamento dei clienti.');
    } finally {
      this.loading = false;
      this.commitInCorso = false;
    }
  }

  // Uscita dal campo: nessuna ricerca e nessun alert. Il testo digitato e non
  // selezionato dai risultati resta come valore libero; se coincide col valore
  // corrente (selezione o valore libero, case-insensitive) non cambia nulla.
  private commitUscita() {
    if (this.commitInCorso) {
      return;
    }
    const testo = this.search.trim();
    if (!testo || this.coincideConSelezione(testo) || this.coincideConValoreLibero(testo)) {
      this.ripristinaVistaCorrente();
      return;
    }
    this.impostaValoreLibero(testo);
  }

  private coincideConSelezione(testo: string): boolean {
    return !!this.anagraficaSelezionata &&
      testo.toLowerCase() === (this.anagraficaSelezionata.ragioneSociale ?? '').toLowerCase();
  }

  // Il backend cerca case-insensitive: un ritocco di sole maiuscole/minuscole
  // non deve essere trattato come testo nuovo (alert già rifiutato).
  private coincideConValoreLibero(testo: string): boolean {
    return !!this.valoreLibero && testo.toLowerCase() === this.valoreLibero.toLowerCase();
  }

  // Nessuna autoselezione: i risultati (anche uno solo) vanno sempre nel dropdown
  // e l'operatore sceglie col click; se non sceglie, all'uscita resta valore libero.
  private async gestisciEsito(testo: string, risultati: Anagrafica[]) {
    if (risultati.length > 0) {
      this.risultati = risultati;
      this.showDropdown = true;
      return;
    }
    await this.chiediCreazione(testo);
  }

  private async chiediCreazione(testo: string) {
    const crea = await this.messageConfirm.presentCustomAlert(
      'Conferma',
      `Nessuna anagrafica trovata per «${testo}». Creare una nuova anagrafica?`,
      'Sì', 'success', () => true
    );
    if (crea) {
      await this.apriNuovaAnagrafica(testo);
    } else {
      this.impostaValoreLibero(testo);
    }
  }

  private ripristinaVistaCorrente() {
    this.showDropdown = false;
    this.risultati = [];
  }

  private impostaValoreLibero(testo: string) {
    this.valoreLibero = testo;
    this.anagraficaSelezionata = null;
    this.anagraficaId = 0;
    this.search = '';
    this.risultati = [];
    this.showDropdown = false;
    this.anagraficaIdChange.emit(0);
    this.ragioneSocialeLibera.emit(testo);
  }

  // Riapertura del campo: mantiene visibile il valore corrente (ragione sociale
  // dell'anagrafica selezionata o valore libero), pronto per essere modificato.
  riapriRicerca() {
    this.annullaCommitPendente();
    this.showDropdown = true;
    this.search = this.anagraficaSelezionata?.ragioneSociale ?? this.valoreLibero;
    // L'input di ricerca è dietro *ngIf: il focus va dato dopo il render della vista,
    // così basta un solo click per iniziare a modificare il valore.
    setTimeout(() => this.searchInput?.setFocus());
  }

  seleziona(anagrafica: Anagrafica) {
    this.annullaCommitPendente();
    this.valoreLibero = '';
    this.anagraficaSelezionata = anagrafica;
    this.anagraficaId = anagrafica.id;
    this.showDropdown = false;
    this.risultati = [];
    this.search = '';
    this.anagraficaIdChange.emit(anagrafica.id);
    this.anagraficaChange.emit(anagrafica);
  }

  svuota() {
    this.annullaCommitPendente();
    this.valoreLibero = '';
    this.anagraficaSelezionata = null;
    this.anagraficaId = 0;
    this.search = '';
    this.showDropdown = false;
    this.risultati = [];
    this.anagraficaIdChange.emit(0);
    this.anagraficaChange.emit(null);
  }

  async apriNuovaAnagrafica(ragioneSocialePrecompilata?: string) {
    this.annullaCommitPendente();
    const componentProps: { isNew: boolean; anagrafica?: Anagrafica } = { isNew: true };
    if (ragioneSocialePrecompilata) {
      const a = emptyAnagrafica();
      a.organizzazioneId = this.organizzazioniService.organizzazioneId;
      a.ragioneSociale = ragioneSocialePrecompilata;
      componentProps.anagrafica = a;
    }
    const modal = await this.modalCtrl.create({
      component: AnagraficaCreateComponent,
      componentProps,
      backdropDismiss: false,
      cssClass: ['custom-modal', 'form-modal']
    });
    await modal.present();
    const { data, role } = await modal.onWillDismiss();
    if (role === 'confirm' && data) {
      this.seleziona(data as Anagrafica);
    }
  }
}
